import { useState, type ChangeEvent, type FormEvent } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

/**
 * Compact partner enquiry form. Posts to the Cloudflare Pages Function at
 * /api/contact, which emails support@quietcorner.io through Resend.
 *
 * Field names and requirements mirror FIELD_RULES in functions/api/contact.js:
 * name, email and company are required, message needs 10+ characters, phone is
 * optional, and `website` is a honeypot that must stay empty.
 *
 * Fields are ruled rather than boxed. Because the shadcn focus ring is removed, focus
 * is signalled three ways: the rule thickens, it turns gold, and the field lifts to
 * white. That keeps a visible keyboard cue that is not carried by colour alone.
 */
const EMPTY = { name: "", email: "", company: "", message: "", phone: "", website: "" };

const fieldClass =
  "bg-transparent border-0 border-b border-quietcove-blue/20 rounded-none px-0 h-11 " +
  "text-quietcove-blue placeholder:text-mut/70 " +
  "focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-b-2 focus:border-gold " +
  "focus:bg-white/70 " +
  "transition-colors duration-200";

const labelClass =
  "block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-quietcove-grey mb-1.5";

const PartnerForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      setIsSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast({
        title: "That didn't send",
        description:
          err instanceof Error
            ? `${err.message}. You can also email support@quietcorner.io.`
            : "Please try again, or email support@quietcorner.io.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSent) {
    return (
      <div className="flex flex-col items-start justify-center min-h-[280px]" role="status">
        <div className="flex items-center gap-4 mb-5">
          <span className="h-px w-10 bg-gold" />
          <Check className="w-5 h-5 text-gold-deep" />
        </div>
        <h3 className="font-display text-2xl font-bold tracking-[-0.015em] text-quietcove-blue mb-3">
          Thanks, that reached us
        </h3>
        <p className="text-quietcove-grey text-lg">We will come back to you within a day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Honeypot: hidden from people, tempting to bots */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <Input
          type="text"
          name="website"
          value={form.website}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
        <div>
          <label htmlFor="pf-name" className={labelClass}>
            Name
          </label>
          <Input
            id="pf-name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            maxLength={100}
            autoComplete="name"
            className={fieldClass}
          />
        </div>
        <div>
          <label htmlFor="pf-email" className={labelClass}>
            Email
          </label>
          <Input
            id="pf-email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            maxLength={255}
            autoComplete="email"
            className={fieldClass}
          />
        </div>
      </div>

      <div className="mt-7">
        <label htmlFor="pf-company" className={labelClass}>
          Venue or company
        </label>
        <Input
          id="pf-company"
          name="company"
          value={form.company}
          onChange={handleChange}
          required
          maxLength={200}
          autoComplete="organization"
          className={fieldClass}
        />
      </div>

      <div className="mt-7">
        <label htmlFor="pf-message" className={labelClass}>
          Tell us about your space
        </label>
        <Textarea
          id="pf-message"
          name="message"
          value={form.message}
          onChange={handleChange}
          required
          minLength={10}
          maxLength={5000}
          placeholder="A lobby, a concourse, a show floor, or a specific event."
          className={`${fieldClass} h-auto min-h-[76px] py-2 resize-none leading-relaxed`}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="group mt-10 inline-flex items-center gap-3 font-display text-lg font-bold text-quietcove-blue border-b-2 border-gold pb-1.5 hover:text-gold-deep transition-colors duration-300 disabled:opacity-60"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending
          </>
        ) : (
          <>
            Start the conversation
            <ArrowRight className="h-5 w-5 text-gold group-hover:translate-x-1.5 transition-transform duration-300" />
          </>
        )}
      </button>
    </form>
  );
};

export default PartnerForm;
